import type {
  UndoableCommand,
  BatchCommand,
  TokenMoveUndoData,
  TokenCreateUndoData,
  TokenDeleteUndoData,
  TokenUpdateUndoData,
  DrawingStrokeUndoData,
  DrawingEraseUndoData,
  DrawingClearUndoData,
  DrawingMoveUndoData,
  DrawingDeleteUndoData,
  FogRevealUndoData,
  FogResetUndoData,
} from '../types';
import type { TextObject } from '@roll1/shared';

// --- Text Undo Data ---

export interface TextCreateUndoData {
  textObject: TextObject;
  send: (msg: Record<string, unknown>) => void;
  addLocal: (obj: TextObject) => void;
  removeLocal: (id: string) => void;
  pageId: string | undefined;
}

export interface TextDeleteUndoData {
  textObject: TextObject;
  send: (msg: Record<string, unknown>) => void;
  addLocal: (obj: TextObject) => void;
  removeLocal: (id: string) => void;
  pageId: string | undefined;
}

export interface TextMoveUndoData {
  textId: string;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  send: (msg: Record<string, unknown>) => void;
  moveLocal: (id: string, x: number, y: number) => void;
  pageId: string | undefined;
}

export interface TextUpdateUndoData {
  textId: string;
  oldChanges: Partial<Pick<TextObject, 'text' | 'fontSize' | 'color' | 'rotation'>>;
  newChanges: Partial<Pick<TextObject, 'text' | 'fontSize' | 'color' | 'rotation'>>;
  send: (msg: Record<string, unknown>) => void;
  updateLocal: (id: string, changes: Partial<Pick<TextObject, 'text' | 'fontSize' | 'color' | 'rotation'>>) => void;
  pageId: string | undefined;
}

/**
 * Создаёт undo-команду для перемещения токена.
 * execute() перемещает в toX/toY, undo() — в fromX/fromY.
 * Обе операции обновляют локальный state и отправляют WS-команду.
 */
export function createTokenMoveCommand(data: TokenMoveUndoData): UndoableCommand {
  return {
    type: 'token:move',
    description: `${data.tokenName}`,
    execute() {
      data.updateToken(data.tokenId, { x: data.toX, y: data.toY });
      data.sendMove(data.tokenId, data.toX, data.toY);
    },
    undo() {
      data.updateToken(data.tokenId, { x: data.fromX, y: data.fromY });
      // undo-флаг: сервер ВЫЧИТАЕТ потраченное движение (movement_spent),
      // если ход был на своём ходу в бою — «не так походил» откатывается целиком.
      data.sendMove(data.tokenId, data.fromX, data.fromY, true);
    },
  };
}

/**
 * Создаёт undo-команду для создания токена.
 * execute() = addToken + sendCreate, undo() = removeToken + sendDelete.
 */
export function createTokenCreateCommand(data: TokenCreateUndoData): UndoableCommand {
  return {
    type: 'token:create',
    description: `${data.token.name}`,
    entityId: data.token.id,
    execute() {
      data.addToken({ ...data.token, imageEl: null });
      data.sendCreate(data.token);
    },
    undo() {
      data.removeToken(data.token.id);
      data.sendDelete(data.token.id);
    },
  };
}

/**
 * Создаёт undo-команду для удаления токена.
 * execute() = removeToken + sendDelete, undo() = addToken + sendCreate.
 */
export function createTokenDeleteCommand(data: TokenDeleteUndoData): UndoableCommand {
  return {
    type: 'token:delete',
    description: `${data.token.name}`,
    execute() {
      data.removeToken(data.token.id);
      data.sendDelete(data.token.id);
    },
    undo() {
      data.addToken({ ...data.token, imageEl: null });
      data.sendCreate(data.token);
    },
  };
}

/**
 * Создаёт undo-команду для обновления свойств токена.
 * execute() = updateToken(newValues) + sendUpdate(newValues),
 * undo() = updateToken(prevValues) + sendUpdate(prevValues).
 */
export function createTokenUpdateCommand(data: TokenUpdateUndoData): UndoableCommand {
  return {
    type: 'token:update',
    description: `${data.tokenName}`,
    execute() {
      data.updateToken(data.tokenId, data.newValues);
      data.sendUpdate(data.tokenId, data.newValues);
    },
    undo() {
      data.updateToken(data.tokenId, data.previousValues);
      data.sendUpdate(data.tokenId, data.previousValues);
    },
  };
}

/**
 * Создаёт undo-команду для рисования штриха.
 * execute() = addStroke + sendStroke, undo() = removeStrokeById + sendErase(strokeId).
 */
export function createDrawingStrokeCommand(data: DrawingStrokeUndoData): UndoableCommand {
  return {
    type: 'drawing:stroke',
    description: 'stroke',
    execute() {
      data.addStroke(data.stroke);
      data.sendStroke(data.stroke);
    },
    undo() {
      data.removeStrokeById(data.stroke.id);
      data.sendErase(data.stroke.id);
    },
  };
}

/**
 * Создаёт undo-команду для стирания штриха.
 * execute() = removeStrokeById + sendErase, undo() = addStroke + sendStroke.
 */
export function createDrawingEraseCommand(data: DrawingEraseUndoData): UndoableCommand {
  return {
    type: 'drawing:erase',
    description: 'erase',
    execute() {
      data.removeStrokeById(data.stroke.id);
      data.sendErase(data.stroke.id);
    },
    undo() {
      data.addStroke(data.stroke);
      data.sendStroke(data.stroke);
    },
  };
}

/**
 * Создаёт undo-команду для очистки всех рисунков.
 * execute() = clearStrokes + sendClear, undo() = setStrokes(snapshot) + sendStroke для каждого.
 */
export function createDrawingClearCommand(data: DrawingClearUndoData): UndoableCommand {
  return {
    type: 'drawing:clear',
    description: 'clear drawings',
    execute() {
      data.clearStrokes();
      data.sendClear();
    },
    undo() {
      data.setStrokes(data.strokes);
      for (const stroke of data.strokes) {
        data.sendStroke(stroke);
      }
    },
  };
}

/**
 * Создаёт undo-команду для перемещения рисунков (drawing selection move).
 * execute() перемещает на (dx, dy), undo() — на (-dx, -dy).
 */
export function createDrawingMoveCommand(data: DrawingMoveUndoData): UndoableCommand {
  return {
    type: 'drawing:move',
    description: `move ${data.strokeIds.length} stroke(s)`,
    execute() {
      data.applyMoveLocal(data.strokeIds, data.dx, data.dy);
      data.sendMove(data.strokeIds, data.dx, data.dy);
    },
    undo() {
      data.applyMoveLocal(data.strokeIds, -data.dx, -data.dy);
      data.sendMove(data.strokeIds, -data.dx, -data.dy);
    },
  };
}

/**
 * Создаёт undo-команду для удаления рисунков (drawing selection delete).
 * execute() удаляет strokes, undo() восстанавливает через drawing:stroke.
 */
export function createDrawingDeleteCommand(data: DrawingDeleteUndoData): UndoableCommand {
  return {
    type: 'drawing:delete',
    description: `delete ${data.strokeIds.length} stroke(s)`,
    execute() {
      data.deleteLocal(data.strokeIds);
      data.sendDelete(data.strokeIds);
    },
    undo() {
      data.restoreLocal(data.deletedStrokes);
      for (const stroke of data.deletedStrokes) {
        data.sendStroke(stroke);
      }
    },
  };
}

/**
 * Создаёт undo-команду для раскрытия тумана (batch reveal).
 * execute() = setRevealed(prev + new) + sendFogRevealBatch(new),
 * undo() = setRevealed(prev) + sendFogReset + sendFogRevealBatch(prev).
 */
export function createFogRevealCommand(data: FogRevealUndoData): UndoableCommand {
  return {
    type: 'fog:reveal',
    description: 'reveal fog',
    execute() {
      data.setRevealed([...data.previousRevealed, ...data.newReveals]);
      data.sendFogRevealBatch(data.newReveals);
    },
    undo() {
      data.setRevealed(data.previousRevealed);
      data.sendFogReset();
      if (data.previousRevealed.length > 0) {
        data.sendFogRevealBatch(data.previousRevealed);
      }
    },
  };
}

/**
 * Создаёт undo-команду для сброса тумана.
 * execute() = clearRevealed + sendFogReset,
 * undo() = setRevealed(prev) + sendFogRevealBatch(prev) + setEnabled + sendFogToggle.
 */
export function createFogResetCommand(data: FogResetUndoData): UndoableCommand {
  return {
    type: 'fog:reset',
    description: 'reset fog',
    execute() {
      data.clearRevealed();
      data.sendFogReset();
    },
    undo() {
      data.setRevealed(data.previousRevealed);
      if (data.previousRevealed.length > 0) {
        data.sendFogRevealBatch(data.previousRevealed);
      }
      if (data.wasEnabled) {
        data.setEnabled(true);
        data.sendFogToggle(true);
      }
    },
  };
}

/**
 * Группирует несколько UndoableCommand в один BatchCommand.
 * execute() выполняет по порядку (0 → N-1), undo() — в обратном (N-1 → 0).
 */
export function createBatchCommand(
  commands: UndoableCommand[],
  description: string,
): BatchCommand {
  return {
    type: 'batch',
    description,
    commands,
    execute() {
      for (const cmd of commands) cmd.execute();
    },
    undo() {
      for (let i = commands.length - 1; i >= 0; i--) commands[i].undo();
    },
  };
}

// --- Text Undo Commands ---

export function createTextCreateCommand(data: TextCreateUndoData): UndoableCommand {
  return {
    type: 'text:create',
    description: data.textObject.text.slice(0, 30),
    execute() {
      data.addLocal(data.textObject);
      data.send({ type: 'text:create', pageId: data.pageId, textObject: { x: data.textObject.x, y: data.textObject.y, text: data.textObject.text, fontSize: data.textObject.fontSize, color: data.textObject.color, rotation: data.textObject.rotation } });
    },
    undo() {
      data.removeLocal(data.textObject.id);
      data.send({ type: 'text:delete', pageId: data.pageId, textId: data.textObject.id });
    },
  };
}

export function createTextDeleteCommand(data: TextDeleteUndoData): UndoableCommand {
  return {
    type: 'text:delete',
    description: data.textObject.text.slice(0, 30),
    execute() {
      data.removeLocal(data.textObject.id);
      data.send({ type: 'text:delete', pageId: data.pageId, textId: data.textObject.id });
    },
    undo() {
      data.addLocal(data.textObject);
      data.send({ type: 'text:create', pageId: data.pageId, textObject: { x: data.textObject.x, y: data.textObject.y, text: data.textObject.text, fontSize: data.textObject.fontSize, color: data.textObject.color, rotation: data.textObject.rotation } });
    },
  };
}

export function createTextMoveCommand(data: TextMoveUndoData): UndoableCommand {
  return {
    type: 'text:move',
    description: 'move text',
    execute() {
      data.moveLocal(data.textId, data.toX, data.toY);
      data.send({ type: 'text:move', pageId: data.pageId, textId: data.textId, x: data.toX, y: data.toY });
    },
    undo() {
      data.moveLocal(data.textId, data.fromX, data.fromY);
      data.send({ type: 'text:move', pageId: data.pageId, textId: data.textId, x: data.fromX, y: data.fromY });
    },
  };
}

export function createTextUpdateCommand(data: TextUpdateUndoData): UndoableCommand {
  return {
    type: 'text:update',
    description: 'update text',
    execute() {
      data.updateLocal(data.textId, data.newChanges);
      data.send({ type: 'text:update', pageId: data.pageId, textId: data.textId, changes: data.newChanges });
    },
    undo() {
      data.updateLocal(data.textId, data.oldChanges);
      data.send({ type: 'text:update', pageId: data.pageId, textId: data.textId, changes: data.oldChanges });
    },
  };
}

// --- Sheet HP Change Undo Command ---

export interface SheetHpChangeUndoData {
  sheetId: string;
  tokenName: string;
  /** Positive magnitude of the change. */
  amount: number;
  /** Direction of the ORIGINAL change. */
  direction: 'damage' | 'healing';
  send: (msg: Record<string, unknown>) => void;
}

/**
 * Undo-команда для изменения HP sheet-bound токена с карты.
 *
 * Само изменение уходит через temp-HP-aware `sheet:apply-damage` /
 * `sheet:apply-healing` (лист авторитетен; сервер зеркалит итоговый hp обратно на
 * токен). Поэтому undo/redo ТОЖЕ идут через эти sheet-команды — никогда через
 * `token:update` — иначе токен рассинхронится с листом.
 *
 * Оговорка: undo реверсит той же величиной в обратную сторону. Temp HP,
 * поглощённый уроном, при undo НЕ восстанавливается (в 5e потраченный temp
 * потерян). Это размен точного восстановления temp на гарантию согласованности
 * токен↔лист.
 */
export function createSheetHpChangeCommand(data: SheetHpChangeUndoData): UndoableCommand {
  const msg = (dir: 'damage' | 'healing') => ({
    type: dir === 'damage' ? 'sheet:apply-damage' : 'sheet:apply-healing',
    sheetId: data.sheetId,
    amount: data.amount,
  });
  const reverse: 'damage' | 'healing' = data.direction === 'damage' ? 'healing' : 'damage';
  return {
    type: 'sheet:hp-change',
    description: data.tokenName,
    execute() {
      data.send(msg(data.direction));
    },
    undo() {
      data.send(msg(reverse));
    },
  };
}

// --- Token Copy Undo Command ---

export interface TokenCopyUndoData {
  token: import('../types').TokenSnapshot;
  addToken: (token: import('../types').Token) => void;
  removeToken: (id: string) => void;
  sendCreate: (token: import('../types').TokenSnapshot) => void;
  sendDelete: (tokenId: string) => void;
}

export function createTokenCopyCommand(data: TokenCopyUndoData): UndoableCommand {
  return {
    type: 'token:copy',
    description: data.token.name,
    entityId: data.token.id,
    execute() {
      data.addToken({ ...data.token, imageEl: null });
      data.sendCreate(data.token);
    },
    undo() {
      data.removeToken(data.token.id);
      data.sendDelete(data.token.id);
    },
  };
}
